import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Select, Table, Tag } from 'antd';
import { FiRefreshCcw, FiShoppingCart, FiUser, FiUsers } from 'react-icons/fi';
import { MdOutlineRequestPage } from 'react-icons/md';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import shareRequestService, { SHARE_REQUEST_STATUSES } from '../../../services/shareRequestService';
import StatCard from '../../../components/StatCard/StatCard';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatInt(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(toNumber(value));
}

function formatAmount(value) {
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(toNumber(value));
}

function formatPriceRange(minPrice, maxPrice) {
  const min = toNumber(minPrice);
  const max = toNumber(maxPrice);
  if (min <= 0 && max <= 0) return '—';
  if (min > 0 && max > 0) {
    return min === max ? formatAmount(min) : `${formatAmount(min)} – ${formatAmount(max)}`;
  }
  if (min > 0) return formatAmount(min);
  if (max > 0) return formatAmount(max);
  return '—';
}

const STATUS_COLORS = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  FULFILLED: 'blue',
};

export default function NewShareRequestPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [stats, setStats] = useState({
    total_requests: 0,
    total_quantity: 0,
    partner_quantity: 0,
    investor_quantity: 0,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchRequests = useCallback(async (page = 1, limit = 10, status = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (status) params.status = status;

      const data = await shareRequestService.getAdminShareRequests(params);
      const requestList = Array.isArray(data?.requests) ? data.requests : [];
      const nextPagination = data?.pagination || {};

      setRows(requestList);
      setStats({
        total_requests: toNumber(data?.stats?.total_requests),
        total_quantity: toNumber(data?.stats?.total_quantity),
        partner_quantity: toNumber(data?.stats?.partner_quantity),
        investor_quantity: toNumber(data?.stats?.investor_quantity),
      });
      setPagination({
        current: toNumber(nextPagination.page) || page,
        pageSize: toNumber(nextPagination.limit) || limit,
        total: toNumber(nextPagination.total) || requestList.length,
      });
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch share requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests(1, pagination.pageSize, statusFilter);
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToInvestorProfile = useCallback(
    (record) => {
      if (!record?.investor_id) return;
      const partnerUuid = record.partner_id ? String(record.partner_id) : '';
      const query = partnerUuid ? `?partner_uuid=${encodeURIComponent(partnerUuid)}` : '';
      navigate(`/investors/${encodeURIComponent(String(record.investor_id))}/profile${query}`);
    },
    [navigate],
  );

  const goToPartnerProfile = useCallback(
    (record) => {
      if (!record?.partner_id) return;
      navigate(`/partner/details/${encodeURIComponent(String(record.partner_id))}`);
    },
    [navigate],
  );

  const profileLinkButton = (name, onClick) => (
    <button
      type="button"
      className="font-medium text-blue-700 hover:text-blue-800 hover:underline"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {name || '—'}
    </button>
  );

  const columns = useMemo(
    () => [
      {
        title: 'Share Name',
        dataIndex: 'share_name',
        key: 'share_name',
        render: (value) => <span className="font-medium text-gray-900">{value || '—'}</span>,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right',
        render: (value) => <span className="font-semibold text-blue-700">{formatInt(value)}</span>,
      },
      {
        title: 'Expected Price Range',
        key: 'price_range',
        align: 'right',
        render: (_, record) => (
          <span className="font-semibold text-emerald-700">
            {formatPriceRange(record.min_price, record.max_price)}
          </span>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
          const normalized = String(status || '').toUpperCase();
          return <Tag color={STATUS_COLORS[normalized] || 'default'}>{normalized || 'UNKNOWN'}</Tag>;
        },
      },
      {
        title: 'Investor',
        dataIndex: 'investor_name',
        key: 'investor_name',
        render: (name, record) =>
          record?.investor_id
            ? profileLinkButton(name, () => goToInvestorProfile(record))
            : <span className="text-gray-600">{name || '—'}</span>,
      },
      {
        title: 'Partner',
        dataIndex: 'partner_name',
        key: 'partner_name',
        render: (name, record) =>
          record?.partner_id
            ? profileLinkButton(name, () => goToPartnerProfile(record))
            : <span className="text-gray-600">{name || '—'}</span>,
      },
      {
        title: 'Remarks',
        dataIndex: 'remarks',
        key: 'remarks',
        render: (value) => <span className="text-gray-600">{value || '—'}</span>,
      },
      {
        title: 'Created On',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => (value ? dayjs(value).format('DD-MMM-YYYY HH:mm') : '—'),
      },
    ],
    [goToInvestorProfile, goToPartnerProfile],
  );

  const fetchRequestsRef = React.useRef(fetchRequests);
  useEffect(() => { fetchRequestsRef.current = fetchRequests; }, [fetchRequests]);

  const statusFilterRef = React.useRef(statusFilter);
  useEffect(() => { statusFilterRef.current = statusFilter; }, [statusFilter]);

  const handleTableChange = useCallback((nextPagination) => {
    fetchRequestsRef.current(
      nextPagination.current,
      nextPagination.pageSize,
      statusFilterRef.current,
    );
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: '', label: 'All statuses' },
      ...SHARE_REQUEST_STATUSES.map((status) => ({ value: status, label: status })),
    ],
    [],
  );

  return (
    <DashboardPageWrapper>
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Shares Request</h1>
          <p className="mt-1 text-sm text-gray-500">
            Buy-demand share requests from partners and investors (out-of-stock products).
          </p>
        </div>
        <Button
          icon={<FiRefreshCcw />}
          onClick={() => fetchRequests(pagination.current, pagination.pageSize, statusFilter)}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert type="error" showIcon className="mb-4" message="Unable to load share requests" description={error} />
      ) : null}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Requests"
          value={formatInt(stats.total_requests)}
          icon={<MdOutlineRequestPage />}
          cardClass="border-blue-100 bg-blue-50/40"
          valueClass="text-blue-700"
        />
        <StatCard
          label="Total Quantity"
          value={formatInt(stats.total_quantity)}
          icon={<FiShoppingCart />}
          cardClass="border-green-100 bg-green-50/40"
          valueClass="text-green-700"
        />
        <StatCard
          label="Partner Quantity"
          value={formatInt(stats.partner_quantity)}
          icon={<FiUser />}
          cardClass="border-purple-100 bg-purple-50/40"
          valueClass="text-purple-700"
        />
        <StatCard
          label="Investor Quantity"
          value={formatInt(stats.investor_quantity)}
          icon={<FiUsers />}
          cardClass="border-amber-100 bg-amber-50/40"
          valueClass="text-amber-700"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Status</label>
          <Select
            allowClear
            placeholder="All statuses"
            className="w-full min-w-[200px]"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || undefined)}
            options={statusOptions.filter((option) => option.value !== '')}
          />
        </div>
        {statusFilter ? (
          <Button type="link" onClick={() => setStatusFilter(undefined)} className="px-0">
            Clear filter
          </Button>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
        <Table
          columns={columns}
          dataSource={rows}
          rowKey={(record) => record.uuid || `${record.share_name}-${record.created_at}`}
          loading={loading}
          scroll={{ x: 'max-content' }}
          className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          onChange={handleTableChange}
          locale={{ emptyText: statusFilter ? 'No share requests for this status' : 'No share requests found' }}
        />
      </div>
    </DashboardPageWrapper>
  );
}
